import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import {
  AUTH_ROUTES,
  AUTH_QUERY_PARAMS,
  AUTH_SOURCES,
  LAWYER_ROUTES,
} from "@/shared/constants/paths";
import posthog from "posthog-js";
import { clearCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import { clearOnboardingData } from "@/shared/utils/onboarding";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";

const LogoutPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isLawyerRedirect = searchParams.get(AUTH_QUERY_PARAMS.FROM) === AUTH_SOURCES.LAWYER;

  useEffect(() => {
    const handleLogout = async () => {
      const redirectUrl = isLawyerRedirect ? LAWYER_ROUTES.LOGIN : AUTH_ROUTES.SIGN_IN;

      try {
        // Track logout event BEFORE clearing session
        trackEvent(EventType.USER_LOGGED_OUT, {
          logout_source: "user_initiated",
        });

        // Clear all React Query cache
        queryClient.clear();

        // Clear lawyer status cache
        clearCachedLawyerStatus();

        // Clear all onboarding state (incl. obit / verification) so it doesn't bleed into the next user
        clearOnboardingData();

        posthog.reset();

        // Sign out from Supabase
        sentryUtils.clearUserContext();
        await supabase.auth.signOut();

        window.location.href = redirectUrl;
      } catch (error) {
        console.error("Logout error:", error);
        sentryUtils.captureError(error, "Logout failed", undefined, "warning");
        window.location.href = redirectUrl;
      }
    };

    handleLogout();
  }, [navigate, isLawyerRedirect]);

  // Show a simple loading message while logout is processing
  return (
    <div className="flex items-center justify-center min-h-screen text-lg">Logging out...</div>
  );
};

export default LogoutPage;
